import React, { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Chip, Col, Row } from '@prism/library'
import { Dropdown } from '../common/custom/prism/dropdown/Dropdown'
import { entryHasOverrideToOffer, FEE_SALE_TYPE_LABEL, getPatEligibilityKey, PAT_ELIGIBILITY_MAX_RETRY_ATTEMPTS } from '../../reducers/PatEligibilityReducer'
import { revertPatSaleType, selectPatSaleType, undoPatSaleType } from '../../actions/PatEligibilityAction'
import { getVehicleSaleTypeLabel, isPatIneligibleVehicle, isSentToAs400 } from '../../utils/vehicle-utils'

import './sale-type-eligibility-status.scss'

// Marks the recommended override in the dropdown label only — stripped back off before the
// selection is turned into a saleType payload, so it never leaks into the stored/sent sale type name.
const RECOMMENDED_OVERRIDE_STAR = ' ★'

function toDropdownOption(saleType, isRecommended) {
    const name = isRecommended ? `${saleType.saleType}${RECOMMENDED_OVERRIDE_STAR}` : saleType.saleType
    return { key: saleType.saleTypeRef, name, disable: false }
}

function stripRecommendedOverrideStar(name) {
    return name.endsWith(RECOMMENDED_OVERRIDE_STAR) ? name.slice(0, -RECOMMENDED_OVERRIDE_STAR.length) : name
}

// Selecting "Lane Default" from the dropdown is equivalent to clicking the old standalone "Revert"
// button — it dispatches revertPatSaleType, not selectPatSaleType. Mirrors the sentinel ref used in
// the reducer's PAT_ELIGIBILITY_REVERT case.
const LANE_DEFAULT_REF = 'LANE_DEFAULT'

function renderInRow(content) {
    return (
        <Row className="mx-0 mt-1 border-top">
            <Col md={12} sm={12} xs={12} className="px-0">
                {/* draggable=false stops the card's native HTML5 drag source (an ancestor row is
                    draggable=true) from ever starting on this control area - the browser resolves
                    drag source by walking up from the exact mousedown target, so this is immune to
                    the mousedown/click timing race that a React-state-based canDrag gate would have. */}
                <div draggable={false}>
                    {content}
                </div>
            </Col>
        </Row>
    )
}

// Third column: for vehicles already sent to AS400 (isSentToAs400) or loaded from a saved draft
// (US2054837), and only when the caller actually wired up onEdit/onCancelEdit — other
// AssignInventoryVehicleItem call sites (e.g. read-only Lane Management detail) don't, so the action
// is omitted there rather than rendered as a dead button.
function renderEditAction(showEditAction, isEditingPat, onEdit, onCancelEdit, t) {
    if (!showEditAction) {
        return null
    }
    return (
        <div className="sale-type-eligibility-status__edit-action">
            <Link
                to={'#'}
                className="sale-type-eligibility-status__edit-link font-weight-bold"
                onClick={e => { e?.preventDefault(); isEditingPat ? onCancelEdit() : onEdit() }}>
                {isEditingPat ? t('CANCEL') : t('EDIT')}
            </Link>
        </div>
    )
}

export function SaleTypeEligibilityStatus({ vin, patAssignmentInstanceId, vehicleSaleType, onRetry, vehicle, onEdit, onCancelEdit, onDropdownOpenChange }) {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const key = getPatEligibilityKey(vin, patAssignmentInstanceId)
    const entry = useSelector(states => states.patEligibilityContext.entriesByKey[key])

    const laneDefaultOption = useMemo(
        () => ({ key: LANE_DEFAULT_REF, name: t('Lane Default'), disable: false }),
        [t]
    )

    const recommendedOverrideRef = entry?.recommendedOverride?.saleTypeRef

    const options = useMemo(() => {
        if (!entry) {
            return [laneDefaultOption]
        }
        const overrideCandidates = [
            ...(entry.recommendedOverride ? [entry.recommendedOverride] : []),
            ...(entry.overrideSaleTypes || [])
        ]
        const seenRefs = new Set()
        const overrides = overrideCandidates.filter((saleType) => {
            if (seenRefs.has(saleType.saleTypeRef)) {
                return false
            }
            seenRefs.add(saleType.saleTypeRef)
            return true
        })
        const overrideOptions = overrides.map((saleType) => toDropdownOption(saleType, saleType.saleTypeRef === recommendedOverrideRef))
        // US2165220: "Select Sale Type" no longer exists as an option in any case — "Lane Default" is
        // always offered as a real dropdown entry, listed first, and is the default whenever nothing
        // else has been picked. Picking it is equivalent to the old standalone "Revert" button.
        return [laneDefaultOption, ...overrideOptions]
    }, [entry, laneDefaultOption, recommendedOverrideRef])

    const selectedOption = useMemo(() => {
        if (entry?.isReverted) {
            return laneDefaultOption
        }
        if (!entry?.selectedSaleType?.saleTypeRef) {
            // US2165220: no recommendation and nothing manually picked yet — default to "Lane Default"
            // rather than an explicit "nothing selected" placeholder (removed along with
            // SELECT_SALE_TYPE_REF). Still null when a recommendedOverride exists but isn't reflected
            // in selectedSaleType yet - the reducer's PAT_ELIGIBILITY_SUCCESS case auto-applies
            // recommendedOverride as selectedSaleType when there's no manual selection, so this is
            // normally unreachable with a real recommendation; kept as a guard so the dropdown never
            // claims "Lane Default" is selected while a recommendation is actually pending/unapplied.
            return entry?.recommendedOverride ? null : laneDefaultOption
        }
        return toDropdownOption(entry.selectedSaleType, entry.selectedSaleType.saleTypeRef === recommendedOverrideRef)
    }, [entry, laneDefaultOption, recommendedOverrideRef])

    // FR-006/FR-006a: no override to surface — nothing more to render beyond the plain default already shown elsewhere.
    const hasNoOverrideToShow = !entryHasOverrideToOffer(entry)

    // isEditingPat mirrors the vehicle actually being under a PAT assignment (patAssignmentInstanceId
    // set) — for an already-sent-to-AS400 vehicle that id is only ever set by clicking Edit, so its
    // presence doubles as "currently editing" without needing separate UI state.
    const isEditingPat = !!patAssignmentInstanceId
    const showEditAction = !!vehicle && !!onEdit && !!onCancelEdit && (vehicle.draftVehicle || isSentToAs400(vehicle))
    const editAction = renderEditAction(showEditAction, isEditingPat, onEdit, onCancelEdit, t)

    // US2165220 (supersedes the 2026-08-28/2026-09-04 change requests): a vehicle that was never
    // PAT-eligible (no weight class, not a Trailer) used to show nothing here at all. There's still no
    // live PAT decision to make for it, so it's now always rendered as the plain "FEE SALE TYPE" /
    // "Lane Default" chip — same shape as the AS400-persisted/draft displays below, not the narrower
    // FR-006i branch below, which only shows once the vehicle is a saved draft or already sent to AS400
    // and would otherwise make this row disappear right after a fresh drop, before either is true.
    // No EDIT here, even when showEditAction would otherwise allow it - editPatSaleType now resolves
    // this vehicle straight to Lane Default without ever calling PAT (AssignVehicleList.js), so EDIT
    // would be a dead click with nothing for the user to review/override. Whatever raw sale type value
    // AS400 happens to have on file for such a vehicle still isn't a real PAT-driven override, so it's
    // never surfaced here. Skipped only when there's no vehicle at all (GO-search-only vehicleSaleType
    // callers that never pass `vehicle`), which must keep rendering as before.
    if (vehicle && isPatIneligibleVehicle(vehicle)) {
        return renderInRow(
            <div className="sale-type-eligibility-status sale-type-eligibility-status--resolved">
                <div className="sale-type-eligibility-status__columns">
                    <span className="sale-type-eligibility-status__label">{t(FEE_SALE_TYPE_LABEL)}</span>
                    <div className="sale-type-eligibility-status__actions">
                        <span className="sale-type-eligibility-status__sale-type-chip">
                            <Chip color="light" bold>{t(getVehicleSaleTypeLabel())}</Chip>
                        </span>
                    </div>
                </div>
            </div>
        )
    }

    if (!entry || !patAssignmentInstanceId) {
        // US2054837 (revised): a vehicle loaded from a saved draft that hasn't been sent to AS400 shows
        // a static, read-only snapshot of its PAT selection at save time — patSelectedSaleType/patLabel
        // as persisted, with no live check, no dropdown, and no patEligibilityContext entry at all.
        // patSelectedSaleType is either '' (nothing to show) or the full { saleType, saleTypeRef }
        // object — only its .saleType name is displayed here. Edit is still offered (mirroring the
        // AS400-persisted case below) so the user can trigger a live check without re-dragging.
        if (vehicle?.draftVehicle && !isSentToAs400(vehicle)) {
            // Bug fix (2026-08-27): patSelectedSaleType === '' (nothing persisted/resolved) still
            // defaults to "Lane Default" for display, same as an explicit FR-006i "resolved as Lane
            // Default" result - the label must default alongside it to 'FEE SALE TYPE' instead of
            // staying blank, so the two are never shown apart (label with no chip, or chip with no
            // label). Renamed from 'SALE TYPE' (2026-09-10) to avoid confusion with the unrelated
            // legacy Sale Type vehicle detail field - a vehicle whose patLabel was already persisted
            // as the old 'SALE TYPE' string (before this rename) still redisplays that old value as-is
            // until it's saved as a draft again; this is intentionally not backfilled.
            return renderInRow(
                <div className="sale-type-eligibility-status sale-type-eligibility-status--resolved">
                    <div className="sale-type-eligibility-status__columns">
                        <span className="sale-type-eligibility-status__label">
                            {t(vehicle.patLabel || FEE_SALE_TYPE_LABEL)}
                        </span>
                        <div className="sale-type-eligibility-status__actions">
                            <span className="sale-type-eligibility-status__sale-type-chip">
                                <Chip color="light" bold>{t(vehicle.patSelectedSaleType?.saleType || 'Lane Default')}</Chip>
                            </span>
                        </div>
                        {editAction}
                    </div>
                </div>
            )
        }

        // No PAT eligibility check has ever run for this vehicle (e.g. it's sitting in the lane from
        // a plain "GO" search, not a drag-and-drop/Auto Assign in this session, and hasn't been put
        // into Edit mode either).
        if (vehicleSaleType === undefined && !showEditAction) {
            return null
        }

        if (vehicleSaleType === undefined) {
            // No persisted sale type to show (vehicle wasn't loaded via "GO" search) — just the Edit
            // action, for an already-sent-to-AS400 vehicle.
            return renderInRow(
                <div className="sale-type-eligibility-status sale-type-eligibility-status--edit-only">
                    <div className="sale-type-eligibility-status__columns">
                        <span className="sale-type-eligibility-status__label" />
                        <div className="sale-type-eligibility-status__actions" />
                        {editAction}
                    </div>
                </div>
            )
        }

        // Show the sale type already persisted in AS400 as plain text in the same label+control slot
        // the dropdown occupies, since there's no live eligibility/override data to drive one —
        // alongside the Edit action when this vehicle is also already sent to AS400.
        return renderInRow(
            <div className="sale-type-eligibility-status sale-type-eligibility-status--resolved">
                <div className="sale-type-eligibility-status__columns">
                    <span className="sale-type-eligibility-status__label">{t(FEE_SALE_TYPE_LABEL)}</span>
                    <div className="sale-type-eligibility-status__actions">
                        <span className="sale-type-eligibility-status__sale-type-chip">
                            <Chip color="light" bold>{t(getVehicleSaleTypeLabel(vehicleSaleType))}</Chip>
                        </span>
                    </div>
                    {editAction}
                </div>
            </div>
        )
    }

    if (entry.status === 'loading') {
        return renderInRow(
            <div className="sale-type-eligibility-status sale-type-eligibility-status--loading">
                <div className="sale-type-eligibility-status__columns">
                    <div className="sale-type-eligibility-status__actions">
                        <span className="sale-type-eligibility-status__spinner" />
                        <span>{t('Checking sale type eligibility…')}</span>
                    </div>
                    {editAction}
                </div>
            </div>
        )
    }

    if (entry.status === 'error') {
        return renderInRow(
            <div className="sale-type-eligibility-status sale-type-eligibility-status--error">
                <div className="sale-type-eligibility-status__columns">
                    <div className="sale-type-eligibility-status__actions">
                        <Link
                            to={'#'}
                            className="sale-type-eligibility-status__retry-link font-weight-bold"
                            onClick={e => { e?.preventDefault(); onRetry && onRetry() }}>
                            {t('RETRY SALE TYPE CHECK')}
                        </Link>
                        {entry.attemptCount >= PAT_ELIGIBILITY_MAX_RETRY_ATTEMPTS && (
                            <span className="sale-type-eligibility-status__retry-later-message">
                                {t('Please try again in a few minutes.')}
                            </span>
                        )}
                    </div>
                    {editAction}
                </div>
            </div>
        )
    }

    if (entry.status === 'resolved') {
        if (hasNoOverrideToShow) {
            // FR-006i (US2165220: always show, same fix as the isPatIneligibleVehicle branch above) —
            // nothing eligible came back from this check, whether triggered by a fresh drag-drop onto a
            // PAT-eligible lane or by Edit: show the same "FEE SALE TYPE" label + "Lane Default" chip
            // (and green --resolved styling) used for the already-persisted display, rather than hiding
            // the row entirely while showEditAction is still false right after the drop. EDIT/Cancel is
            // still gated by the normal showEditAction rule (editAction resolves to null itself when it
            // doesn't apply).
            return renderInRow(
                <div className="sale-type-eligibility-status sale-type-eligibility-status--resolved">
                    <div className="sale-type-eligibility-status__columns">
                        <span className="sale-type-eligibility-status__label">{t(FEE_SALE_TYPE_LABEL)}</span>
                        <div className="sale-type-eligibility-status__actions">
                            <span className="sale-type-eligibility-status__sale-type-chip">
                                <Chip color="light" bold>{t(getVehicleSaleTypeLabel())}</Chip>
                            </span>
                        </div>
                        {editAction}
                    </div>
                </div>
            )
        }

        // Revert is disabled (not hidden) once already reverted — re-enabled as soon as the user picks
        // something else from the dropdown, which clears isReverted.
        const isRevertDisabled = entry.isReverted
        // Undo/"Recommend" is disabled whenever the current selection already matches the value the
        // dropdown had the first time it was rendered for this assignment — i.e. there's nothing left
        // to undo to — whether that's because of a Revert or a manual dropdown change (FR-004/FR-005;
        // research.md Decision 3).
        const currentRef = entry.selectedSaleType?.saleTypeRef ?? null
        const firstRenderedRef = entry.firstRenderedValue?.saleTypeRef ?? null
        const showUndo = entry.isReverted || currentRef !== firstRenderedRef
        const isUndoDisabled = !showUndo
        // The label falls back to the original AUTO-UPDATED/FEE SALE TYPE state whenever the current
        // selection matches the first-rendered value again — e.g. the user manually picks "Lane
        // Default" or the recommended override back — not just on an explicit Undo click.
        // US2165220: the untouched/no-recommendation state (no recommendedOverride, nothing picked)
        // is no longer "NEEDS ASSIGNMENT" - entryRequiresSaleTypeSelection doesn't gate Send to AS400
        // for it any more, so labeling it as a pending action is misleading. FEE_SALE_TYPE_LABEL
        // matches the neutral label used everywhere else Lane Default already applies with nothing
        // required of the user.
        const label = entry.isReverted
            ? t('REVERTED')
            : showUndo
                ? t('ASSIGNED')
                : entry.recommendedOverride ? t('AUTO-UPDATED') : t(FEE_SALE_TYPE_LABEL)

        return renderInRow(
            <div className="sale-type-eligibility-status sale-type-eligibility-status--resolved">
                <div className="sale-type-eligibility-status__columns">
                    <span className="sale-type-eligibility-status__label">{label}</span>
                    <div className="sale-type-eligibility-status__actions">
                        <Dropdown
                            options={options}
                            selectedOption={selectedOption}
                            onOpenChange={onDropdownOpenChange}
                            onChange={(newSelectedOption) => {
                                // Bug fix (2026-08-19): any Select/Revert here invalidates whatever
                                // "ready to send" claim a prior Save As Draft made for this vehicle -
                                // clear it now so it can't linger, or reappear later just because this
                                // change happens to zero out the pending/failed count again.
                                window.setSaveDraftVehicleMessage(null)
                                if (newSelectedOption.key === LANE_DEFAULT_REF) {
                                    dispatch(revertPatSaleType(vin, patAssignmentInstanceId))
                                    return
                                }
                                const saleType = { saleType: stripRecommendedOverrideStar(newSelectedOption.name), saleTypeRef: newSelectedOption.key }
                                dispatch(selectPatSaleType(vin, patAssignmentInstanceId, saleType))
                            }}
                        />
                        <Link
                            to={'#'}
                            className={`sale-type-eligibility-status__revert-link font-weight-bold${isRevertDisabled ? ' sale-type-eligibility-status__revert-link--disabled' : ''}`}
                            aria-disabled={isRevertDisabled}
                            onClick={e => { e?.preventDefault(); if (!isRevertDisabled) { window.setSaveDraftVehicleMessage(null); dispatch(revertPatSaleType(vin, patAssignmentInstanceId)) } }}>
                            {t('REVERT')}
                        </Link>
                        <Link
                            to={'#'}
                            className={`sale-type-eligibility-status__undo-link font-weight-bold${isUndoDisabled ? ' sale-type-eligibility-status__undo-link--disabled' : ''}`}
                            aria-disabled={isUndoDisabled}
                            onClick={e => { e?.preventDefault(); if (!isUndoDisabled) { window.setSaveDraftVehicleMessage(null); dispatch(undoPatSaleType(vin, patAssignmentInstanceId)) } }}>
                            {t('RECOMMEND')}
                        </Link>
                    </div>
                    {editAction}
                </div>
            </div>
        )
    }

    return null
}
